import { useMemo, useEffect, useState } from "react";
import {
 collection,
 getDocs,
 doc,
 updateDoc,
 arrayUnion,
 arrayRemove,
 where,
 query,
 limit as firestoreLimit,
} from "firebase/firestore";
import { databasePromise, getAuthAsync } from "configuration";

export const useRelatedDeals = (currentDeal, maxResults = 4) => {
 const [deals, setDeals] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState(null);

 useEffect(() => {
  if (!currentDeal?.id) return;
  let cancelled = false;

  const fetchRelated = async () => {
   try {
    const db = await databasePromise;
    if (cancelled) return;

    const snapshot = await getDocs(
     query(
      collection(db, "deals"),
      where("restaurantId", "==", currentDeal.restaurantId),
      where("status", "in", ["active", "pendingEdit"]),
      firestoreLimit(20),
     ),
    );

    if (cancelled) return;
    setDeals(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    setLoading(false);
   } catch (err) {
    if (!cancelled) {
     setError(err.message);
     setLoading(false);
    }
   }
  };

  fetchRelated();
  return () => {
   cancelled = true;
  };
 }, [currentDeal?.id, currentDeal?.restaurantId]);

 const relatedDeals = useMemo(() => {
  if (!currentDeal || !deals.length) return [];

  const otherDeals = deals.filter((deal) => deal.id !== currentDeal.id);

  // Same restaurant first
  let exactMatches = currentDeal.restaurantName
   ? otherDeals.filter((d) => d.restaurantName === currentDeal.restaurantName)
   : [];

  // Same categories
  const categoryMatches = currentDeal.categories?.length
   ? otherDeals.filter(
      (d) =>
       d.id !== currentDeal.id &&
       !exactMatches.find((e) => e.id === d.id) &&
       d.categories?.some((cat) => currentDeal.categories.includes(cat)),
     )
   : [];

  // Same price range (within 20%)
  const priceMatches = currentDeal.price
   ? otherDeals.filter((d) => {
      if (!d.price) return false;
      if (exactMatches.find((e) => e.id === d.id)) return false;
      if (categoryMatches.find((c) => c.id === d.id)) return false;
      const diff = Math.abs(d.price - currentDeal.price) / currentDeal.price;
      return diff <= 0.2;
     })
   : [];

  // Fill remaining with other deals
  const combined = [...exactMatches, ...categoryMatches, ...priceMatches];
  if (combined.length < maxResults) {
   const remaining = otherDeals.filter(
    (d) => !combined.find((c) => c.id === d.id),
   );
   combined.push(...remaining);
  }

  return combined.slice(0, maxResults);
 }, [currentDeal, deals, maxResults]);

 async function likeDeal(dealId) {
  try {
   const auth = await getAuthAsync();
   if (!auth.currentUser)
    throw new Error("User must be signed in to like a deal");
   const db = await databasePromise;
   await updateDoc(doc(db, "deals", dealId), {
    likedBy: arrayUnion(auth.currentUser.uid),
   });
  } catch (error) {
   console.error("Error liking deal:", error);
  }
 }

 async function unlikeDeal(dealId) {
  try {
   const auth = await getAuthAsync();
   if (!auth.currentUser)
    throw new Error("User must be signed in to like a deal");
   const db = await databasePromise;
   await updateDoc(doc(db, "deals", dealId), {
    likedBy: arrayRemove(auth.currentUser.uid),
   });
  } catch (error) {
   console.error("Error unliking deal:", error);
  }
 }

 return { relatedDeals, loading, error, likeDeal, unlikeDeal };
};
